/**
 * The open RSVP link (1.7.0, D-1.7.0-1): `/e/<slug>` — one address per event that anyone can use, any number of
 * times, with no sign-in (the address on the flyer's QR code). Pure rules shared by the API and the app: the slug,
 * the short form's schema, how a typed apartment and name are matched to a tenant record, and the page's shapes.
 *
 * The form asks first "I am: a tenant / a volunteer" (1.7.4 — there is no "guest": a guest exists only inside a
 * tenant's answer). A tenant: name, apartment, bringing a guest (no / 1 / 2 as the event allows, names optional),
 * in a raffle whether one more person from the household would like a ticket, and an optional email or phone.
 * A volunteer: name and email (no apartment). Sending it is a yes. There is no food question of any kind (the 1.3.0 test reads OPEN_RSVP_FIELDS too).
 */
import { z } from 'zod';
import type { SpotRule } from './events';
import type { GuestsAllowed } from './event-places';

/* ------------------------------------------------------------------------------------------------
 * The slug: the event's name in plain letters, then the event's own id — nothing is stored, the same
 * event always has the same address, and a flyer printed today still works if the title is corrected.
 * ---------------------------------------------------------------------------------------------- */

const plain = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const idPart = (eventId: string) =>
  eventId
    .replace(/^(EVT|SRV)-/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export function eventSlug(name: string, eventId: string): string {
  const words = plain(name.replace(/^SECURITY-TEST\s+/i, ''))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return [words, idPart(eventId)].filter(Boolean).join('-');
}

export const OPEN_SLUG = /^[a-z0-9][a-z0-9-]{3,119}$/;

/** True when the slug ends with this event's id (the words before it are for people, not for the match). */
export function slugIsFor(slug: string, eventId: string): boolean {
  const id = idPart(eventId);
  return !!id && OPEN_SLUG.test(slug) && (slug === id || slug.endsWith(`-${id}`));
}

/** The event id a slug ends with (`…-20261024-a3f9c1` → `EVT-20261024-A3F9C1`); null when it has none. */
export function eventIdOfSlug(slug: string): string | null {
  if (!OPEN_SLUG.test(slug)) return null;
  const parts = slug.split('-');
  if (parts.length < 2) return null;
  return `EVT-${parts.slice(-2).join('-').toUpperCase()}`;
}

/* ------------------------------------------------------------------------------------------------
 * The form
 * ---------------------------------------------------------------------------------------------- */

export const OPEN_GUEST_MAX = 2;

const text = (max: number) => z.string().trim().max(max);

/** 1.7.4 — who is answering. There is no third choice. */
export const OPEN_IAM = ['tenant', 'volunteer'] as const;
export type OpenIam = (typeof OPEN_IAM)[number];

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const OpenRsvpSchema = z
  .object({
  iam: z.enum(OPEN_IAM).optional().default('tenant'),
  name: text(120).min(2, 'required'),
  /** A tenant's apartment (a volunteer has none). */
  apartment: text(20).optional().default(''),
  /** A volunteer's email — with the name, how they are found on the volunteer list. */
  email: text(254).optional().default(''),
  /** A raffle with the household extra on: one more person from the household would like a ticket. */
  householdExtra: z.boolean().optional().default(false),
  /** Bringing a guest? No / 1 / 2. */
  guests: z.number().int().min(0).max(OPEN_GUEST_MAX).default(0),
  /** Optional — "we always encourage names". */
  guestNames: z.array(text(60)).max(OPEN_GUEST_MAX).optional().default([]),
  /** Optional: an email or a phone number, kept on the answer for the office. */
  reach: text(120).optional().default(''),
  /** 1.7.5, a tenant on an event where something is served: true = "Please bring it to me (I can't come down)". */
  delivery: z.boolean().optional(),
  })
  .superRefine((a, ctx) => {
    if (a.iam === 'tenant' && !a.apartment) ctx.addIssue({ code: 'custom', path: ['apartment'], message: 'required' });
    if (a.iam === 'volunteer' && !EMAIL_SHAPE.test(a.email)) ctx.addIssue({ code: 'custom', path: ['email'], message: a.email ? 'email' : 'required' });
  });
export type OpenRsvpInput = z.input<typeof OpenRsvpSchema>;
export type OpenRsvpParsed = z.output<typeof OpenRsvpSchema>;

/** The fields the open form shows (the no-food test reads this list with the labels). */
export const OPEN_RSVP_FIELDS = ['iam', 'name', 'apartment', 'email', 'guests', 'guestNames', 'householdExtra', 'reach', 'delivery'] as const;

/* ------------------------------------------------------------------------------------------------
 * Matching the typed apartment and name to a tenant record: the apartment exactly, the name loosely
 * ---------------------------------------------------------------------------------------------- */

/** "apt 12-b", "#12B", "012b" → "12B". */
export function normApartment(s: string): string {
  return plain(s)
    .toUpperCase()
    .replace(/\b(APARTMENT|APARTAMENTO|APTO|APT|UNIT|UNIDAD)\b\.?/g, '')
    .replace(/[^A-Z0-9]/g, '')
    .replace(/^0+(?=[A-Z0-9])/, '');
}

const SMALL_WORDS = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'y', 'mr', 'mrs', 'ms', 'sr', 'sra', 'srta', 'jr', 'the', 'and', 'security', 'test']);

export function nameTokens(s: string): string[] {
  return plain(s)
    .split(/[^a-z]+/)
    .filter((w) => w.length >= 2 && !SMALL_WORDS.has(w));
}

/** One letter wrong, missing or extra (words of four letters or more). */
function oneOff(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

const wordsAlike = (a: string, b: string) => a === b || (a.length >= 4 && b.length >= 4 && oneOff(a, b)) || (Math.min(a.length, b.length) >= 3 && (a.startsWith(b) || b.startsWith(a)));

/** How many of the typed words are a name on the record (the same word, one letter off, or a short form of it). */
export function nameScore(typed: string, recordNames: readonly string[]): number {
  const have = recordNames.flatMap(nameTokens);
  return nameTokens(typed).filter((w) => have.some((h) => wordsAlike(w, h))).length;
}

export interface MatchCandidate {
  tenantId: string;
  unit: string;
  /** Every name the record knows for this apartment: the tenant's, the preferred name, the household's. */
  names: string[];
}

/** 1.7.4 — the volunteer this answer belongs to: the email exactly (capitals do not matter) and at least one word of the name; else null (review). */
export function matchVolunteer(typed: { name: string; email: string }, volunteers: readonly { volunteerId: string; email: string; names: string[] }[]): string | null {
  const email = typed.email.trim().toLowerCase();
  if (!email) return null;
  for (const v of volunteers) if (v.email.trim().toLowerCase() === email && nameScore(typed.name, v.names) > 0) return v.volunteerId;
  return null;
}

/** The tenant this answer belongs to: the apartment exactly and at least one word of the name; else null (review). */
export function matchTenant(typed: { name: string; apartment: string }, candidates: readonly MatchCandidate[]): string | null {
  const apt = normApartment(typed.apartment);
  if (!apt) return null;
  let best: { id: string; score: number } | null = null;
  for (const c of candidates) {
    if (normApartment(c.unit) !== apt) continue;
    const score = nameScore(typed.name, c.names);
    if (score > 0 && (!best || score > best.score)) best = { id: c.tenantId, score };
  }
  return best?.id ?? null;
}

/* ------------------------------------------------------------------------------------------------
 * The page
 * ---------------------------------------------------------------------------------------------- */

/** Which side an open link answers for (13 Oct 2026): tenants and their guests, or volunteers — never both. */
export const OPEN_SIDES = ['tenants', 'volunteers'] as const;
export type OpenSide = (typeof OPEN_SIDES)[number];
/** The volunteers' link is the tenants' address with this after it: `/e/<slug>/v`. */
export const VOLUNTEER_SUFFIX = 'v';
export const openSideOf = (v: unknown): OpenSide => (v === 'v' || v === 'volunteers' ? 'volunteers' : 'tenants');

export interface OpenRsvpPage {
  state: 'ok' | 'closed' | 'cancelled' | 'off' | 'not-found';
  /** The side this link answers for (absent on an older answer: tenants). */
  side?: OpenSide;
  event: {
    name: string;
    dayKey: string;
    minutes: number | null;
    endMinutes: number | null;
    place: string;
    address: string;
    directions: { google: string; apple: string } | null;
    /** What it is, what to bring, the accessibility line — the event's own words. */
    description: string;
    rule: SpotRule;
    /** People with a spot so far, and the limit (the room, the N, the tickets); null = no limit. */
    soFar: number;
    limit: number | null;
    /** No spot is left: a new answer joins the waiting list. */
    full: boolean;
    drawn: boolean;
    /** 1.7.4 — what the form asks: guests a tenant may announce (0 = not asked), the household extra (a raffle), guests of a volunteer (0 unless the event allows them). */
    ask: { guests: GuestsAllowed; householdExtra: boolean; volunteerGuests: GuestsAllowed; /** 1.7.5 — a tenant is asked "come down, or bring it to you?" */ delivery?: boolean };
  } | null;
  officePhone: string | null;
}

export interface OpenRsvpResult {
  ok: boolean;
  message: string;
  /** People this answer brings (the person and their guests). */
  attending?: number;
  waiting?: boolean;
  waitingRank?: number | null;
  page?: OpenRsvpPage;
}

/** Guest names as one line in the RSVP's notes ("Guests: A, B"); an earlier such line is replaced. */
export function guestNamesOf(notes: string): string[] {
  const line = notes.split('\n').find((l) => /^Guests?: /i.test(l.trim()));
  return line
    ? line
        .trim()
        .replace(/^Guests?: /i, '')
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean)
    : [];
}

export function withGuestNames(notes: string, names: readonly string[]): string {
  const kept = notes
    .split('\n')
    .filter((l) => !/^Guests?: /i.test(l.trim()))
    .join('\n')
    .trim();
  const line = names.map((n) => n.trim()).filter(Boolean).join(', ');
  return [kept, line ? `Guests: ${line}` : ''].filter(Boolean).join('\n').slice(0, 1000);
}
